// @ts-nocheck
/* eslint-disable import/no-unassigned-import */
/* eslint-disable import/prefer-default-export */
import { createBrowserHistory } from 'history';
import { MemoryRouter, Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createRoot, hydrateRoot } from 'react-dom/client';
import {
    components as commonComponents,
    analytics as commonAnalytics,
    logger as commonLogging,
    graphql as commonGraphql,
} from '@sitebuilder/common';
import { authentication, configureStore } from '@sitebuilder/admin-app';
import { RootDocumentComp, WidgetFactory } from '@sitebuilder/widget-lib';
import App from './App';
import * as serviceWorker from './serviceWorker';
import clientSettings from './client-settings';
import { AuthProvider } from './auth/AuthContext';
import { userManager } from './auth/oidcConfig';

const login = async () => {
    await userManager.signinRedirect();
};

const logout = async () => {
    await userManager.signoutRedirect();
};

declare global {
    interface Window {
        // datafront attributes
        dfSb: {
            __PRELOADED_STATES__: Record<string, commonComponents.AppContextShape>;
            LOG_LEVEL: commonLogging.LogLevels;
            ANALYTICS_ENABLED?: boolean;
            ANALYTICS_ENGINE?: string;
        };
    }
}

window.dfSb = window.dfSb || {};
window.dfSb.LOG_LEVEL = window.dfSb.LOG_LEVEL || commonLogging.LogLevels.Info;
const analyticsEngine = window.dfSb.ANALYTICS_ENGINE || 'none';
const logger = new commonLogging.Logger();
logger.logger = console;
logger.getLogLevel = () => window.dfSb.LOG_LEVEL;

if (window.location.href.indexOf('silent-renew') >= 0) {
    // Silent renew endpoint called
    processSilentRenew();
} else {
    // we want to render multiple apps on the same page
    // so first we find all instances of the apps, which are <div>
    // elements with data-df-app-id attribute then within each one,
    // we cna look for a preloaded state and hydrate the app
    const appInstances = document.querySelectorAll('[data-df-app-id]');
    // into an array
    const appInstancesArray = Array.from(appInstances);

    if (appInstances.length > 0) {
        for (const appInstance of appInstancesArray) {
            const appId = appInstance.getAttribute('data-df-app-id');
            if (!appId) {
                continue;
            }

            const preloadedState = window[
                `dfsb-preloaded-state`
            ] as commonComponents.AppContextShape;
            const initialStates = window.dfSb.__PRELOADED_STATES__ as Record<
                string,
                commonComponents.AppContextShape
            >;

            let initialState: commonComponents.AppContextShape = {};

            if (initialStates) {
                initialState = initialStates[appId];
            } else if (preloadedState) {
                initialState = preloadedState;
            }

            const appElement = document.getElementById(appId);

            const root = document.getElementById(`${appId}-root`);
            const fullUrl = new URL(
                appElement?.getAttribute('data-df-url') || window.location.href
            );
            const apptype = appElement?.getAttribute('data-df-type') || 'page';

            // get the base path, e.g. from https://example.com/path/to/page?query=string
            // get https://example.com/
            const origin = fullUrl.origin;

            if (root) {
                try {
                    if (apptype === 'snippet') {
                        hydrateRoot(
                            root,
                            <commonAnalytics.AnalyticsProvider value={analyticsEngine}>
                                <commonComponents.AppContext.AppContextProvider
                                    {...{
                                        ...initialState,
                                        RootDocument: RootDocumentComp,
                                        widgetFactory: new WidgetFactory(),
                                        graphQLClient: new commonGraphql.QueryApiClient(),
                                        logger,
                                    }}
                                >
                                    <MemoryRouter
                                        basename={`${origin}/`}
                                        initialEntries={[fullUrl.pathname + fullUrl.search]}
                                    >
                                        <commonComponents.PageViewer.default />
                                    </MemoryRouter>
                                </commonComponents.AppContext.AppContextProvider>
                            </commonAnalytics.AnalyticsProvider>,
                            {
                                identifierPrefix: `${appId}-`,
                            }
                        );
                    } else {
                        const browserHistory = createBrowserHistory({
                            basename: initialState.baseUrl || undefined,
                            forceRefresh: true,
                        });
                        hydrateRoot(
                            root,
                            <commonAnalytics.AnalyticsProvider value={analyticsEngine}>
                                <commonComponents.AppContext.AppContextProvider
                                    {...{
                                        ...initialState,
                                        RootDocument: RootDocumentComp,
                                        widgetFactory: new WidgetFactory(),
                                        graphQLClient: new commonGraphql.QueryApiClient(),
                                        logger,
                                    }}
                                >
                                    <Router history={browserHistory}>
                                        <commonComponents.PageViewer.default />
                                    </Router>
                                </commonComponents.AppContext.AppContextProvider>
                            </commonAnalytics.AnalyticsProvider>,
                            {
                                identifierPrefix: `${appId}-`,
                            }
                        );
                    }
                } catch (error) {
                    console.error('Error hydrating app', error);
                }
            }
        }
    } else {
        const rootElement = document.getElementById('root');
        if (!rootElement) {
            throw new Error('Root element not found');
        }
        const root = createRoot(rootElement);

        userManager.getUser().then((user) => {
            const baseUrl =
                document.getElementsByTagName('base')[0].getAttribute('href') || undefined;
            const browserHistory = createBrowserHistory({
                basename: baseUrl || undefined,
                forceRefresh: true,
            });
            const { store, history } = configureStore(browserHistory, baseUrl, { auth: { user } });
            console.log('user from index getUser', user);
            root.render(
                <commonAnalytics.AnalyticsProvider value={analyticsEngine}>
                    <AuthProvider user={user}>
                        <Router history={browserHistory}>
                            <commonComponents.AppContext.AppContextProvider
                                RootDocument={RootDocumentComp}
                                widgetFactory={new WidgetFactory()}
                                logger={logger}
                                graphQLClient={new commonGraphql.QueryApiClient()}
                            >
                                <Provider store={store}>
                                    <App user={user} />
                                </Provider>
                            </commonComponents.AppContext.AppContextProvider>
                        </Router>
                    </AuthProvider>
                </commonAnalytics.AnalyticsProvider>
            );
        });
    }
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
